import { useEffect, useMemo, useState } from 'react';
import { useDateService } from '../../utils/hooks/useDateService';
import useShortPolling from '../../utils/hooks/useShortPolling/useShortPolling';
import type { LoadingParams, SearchParams } from '../ListContext';
import getLoadingParams from './listBuilderHelper';

interface ListUpdatingHelperParams {
  loadData: (params: LoadingParams, isNeedToUpdateLoading: boolean) => Promise<any>;
  params: SearchParams;
  pageSize?: number;
  autoUpdateTime?: number;
}

const useListDataUpdating = ({ loadData, params, pageSize, autoUpdateTime }: ListUpdatingHelperParams) => {
  const { getCurrentDateTime } = useDateService();
  const [data, setData] = useState({
    items: [],
    totalPages: 0,
    totalElements: 0,
    lastUpdate: '',
  });
  const loadingParams = useMemo(() => getLoadingParams(params, pageSize), [pageSize, params]);

  const handleLoad = async (isNeedToUpdateLoading = true) => {
    const response = await loadData(loadingParams, isNeedToUpdateLoading);
    setData({
      items: response.result || [],
      totalPages: response.totalPages || 0,
      totalElements: response.totalElements || 0,
      lastUpdate: getCurrentDateTime().toISOString(),
    });
  };

  useEffect(() => {
    handleLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingParams]);

  useShortPolling({
    callback: () => handleLoad(false),
    enabled: Boolean(autoUpdateTime),
    executeImmediately: false,
    interval: autoUpdateTime ?? 0,
  });

  return {
    data,
    reloadData: handleLoad,
  };
};

export default useListDataUpdating;
